import { useState, useEffect, useCallback } from 'react'
import { ApolloClient } from '@apollo/client'
import { getProjectHoverContentByGuid } from '@/services/ProjectsService'
import { UnifiedProjectHoverContent } from '@/services/ProjectsService/types'
import { useSafeAnalytics } from '@/utils/analytics'
import { logger } from '@/utils/logging'

interface UseHoverContentProps {
  isVisible: boolean
  guid?: string | null
  projectSlug?: string | null
  client: ApolloClient<object>
}

export const useHoverContent = ({ isVisible, guid, projectSlug, client }: UseHoverContentProps) => {
  const [hoverContent, setHoverContent] = useState<UnifiedProjectHoverContent | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isVisible || !guid || !client || hoverContent) return

    const fetchHoverContent = async () => {
      setIsLoading(true)
      try {
        const result = await getProjectHoverContentByGuid(guid, client, projectSlug)
        setHoverContent(result.hoverDataContent)

        if (result.error) {
          logger().error('Failed to fetch hover content', { guid, error: result.error })
        }
      } catch (error) {
        logger().error('Error fetching hover content', { guid, error })
        setHoverContent(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchHoverContent()
  }, [isVisible, guid, client, hoverContent, projectSlug])

  useEffect(() => {
    setHoverContent(null)
    setIsLoading(false)
  }, [guid])

  return { hoverContent, isLoading }
}

export const useModalVisibility = (isVisible: boolean) => {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (isVisible) {
      timeoutId = setTimeout(() => {
        setShouldShow(true)
      }, 1000)
    } else {
      setShouldShow(false)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isVisible])

  return { shouldShow, setShouldShow }
}

export const useVideoPlayer = (isVisible: boolean) => {
  const [playerControls, setPlayerControls] = useState<{ unmute: () => void; mute: () => void } | null>(null)
  const [isPlayerMuted, setIsPlayerMuted] = useState(true)
  const [playerKey, setPlayerKey] = useState(0)
  const { track } = useSafeAnalytics()

  useEffect(() => {
    if (!isVisible) {
      setPlayerControls(null)
      setIsPlayerMuted(true)
      setPlayerKey((prev) => prev + 1)
    }
  }, [isVisible])

  const handleUnmute = useCallback(() => {
    if (playerControls) {
      playerControls.unmute()
      setIsPlayerMuted(false)
      track('Watch Hover Modal Preview Unmuted')
    }
  }, [playerControls, track])

  const handlePlayerReady = useCallback((controls: { unmute: () => void; mute: () => void }) => {
    setPlayerControls(controls)
  }, [])

  return {
    playerControls,
    isPlayerMuted,
    playerKey,
    handleUnmute,
    handlePlayerReady,
  }
}
